import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import type { Gebaeude } from "./types";

const useStyles = makeStyles({
  wrap: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  intro: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  card: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    backgroundColor: BRAND.white,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    fontFamily: BRAND.fontAccent,
    fontSize: "0.95rem",
    fontWeight: 600,
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  labelInput: {
    flexGrow: 1,
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "transparent"),
    backgroundColor: "transparent",
    outlineWidth: 0,
    transitionProperty: "border-color, background-color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor("#d4cfd9"),
    },
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: BRAND.bg,
    },
  },
  removeBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.muted,
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.1rem",
    fontWeight: 700,
    width: "28px",
    height: "28px",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "#f5eef6",
      color: BRAND.purple,
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.3,
    },
  },
  addBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 700,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "dashed", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    transitionProperty: "background-color",
    transitionDuration: "120ms",
    alignSelf: "flex-start",
    ":hover": {
      backgroundColor: "#f5eef6",
    },
  },
});

type Props = {
  gebaeude: Gebaeude[];
  onRename: (id: string, label: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
};

const GebaeudeRegistry = ({ gebaeude, onRename, onAdd, onRemove }: Props) => {
  const styles = useStyles();
  return (
    <div className={styles.wrap}>
      <p className={styles.intro}>
        Bitte erfassen Sie hier alle Gebäude auf dem Grundstück. Sie können später jedes Gebäude einzeln ausfüllen.
      </p>
      <div className={styles.list}>
        {gebaeude.map((g, idx) => (
          <div key={g.id} className={styles.card}>
            <span className={styles.badge}>{idx + 1}</span>
            <input
              className={styles.labelInput}
              value={g.label}
              placeholder={`Gebäude ${idx + 1}`}
              onChange={(e) => onRename(g.id, e.target.value)}
            />
            <button
              type="button"
              className={styles.removeBtn}
              disabled={gebaeude.length <= 1}
              onClick={() => onRemove(g.id)}
              aria-label="Gebäude entfernen"
              title="Entfernen"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button type="button" className={styles.addBtn} onClick={onAdd}>
        + Weiteres Gebäude hinzufügen
      </button>
    </div>
  );
};

export default GebaeudeRegistry;
